import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  ExportMenuItems,
  Flex,
  FormControlSizes,
  IconName,
  MARKET_ORDER,
  MarketOrder,
  MixpanelEvent,
  WSBlotterTableMaxRecordsReachedWarning,
  filterByColumnMainMenuItems,
  isTalosUser,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMarketOrderColumns,
  useMixpanel,
  usePersistedBlotterTable,
  useUserContext,
  useWsBlotterTable,
  type BlotterTableSort,
  type Column,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useMarketOrdersMenu } from './MarketOrdersMenu';
import {
  colIDToFilterBuilderKey,
  onlyServerFilterKeys,
  useMarketOrderFilter,
  type MarketOrderFilter,
} from './useMarketOrderFilter';

export interface MarketOrdersBlotterTableProps {
  /** Unique id per blotter instance */
  blotterID: string;
  /** ID for common blotter type persistence; usually a subset of the blotterId to represent blotter area 'type' */
  blotterPersistID?: string;
  tabLabel?: string;
  orderID?: string;
  rfqID?: string;
  parentOrderID?: string;
  parentRFQID?: string;

  defaultColumns?: (Column | keyof MarketOrder)[];
  defaultFilter?: MarketOrderFilter;
  defaultSort?: BlotterTableSort<MarketOrder>;
  persistFilter?: boolean;
  persistSort?: boolean;
  onCloneTab?: () => void;
  initialIsOpen?: boolean;
  source?: 'trading' | 'monitoring' | 'dealer-monitoring';
}

export function MarketOrdersBlotterTable({
  parentOrderID,
  parentRFQID,
  blotterID,
  blotterPersistID = blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  defaultSort = '-SubmitTime',
  persistFilter,
  onCloneTab,
  initialIsOpen,
  source,
}: MarketOrdersBlotterTableProps) {
  const { getConfig } = useOrgConfiguration();
  const mixpanel = useMixpanel();
  const columns = useMarketOrderColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(
    blotterPersistID,
    { filter: defaultFilter },
    { persistFilter }
  );

  const marketOrderFilter = useMarketOrderFilter({
    persistedBlotterTable,
    includeStatusFilter: true,
    includeIDFilter: parentOrderID == null && parentRFQID == null,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = marketOrderFilter;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });
  const marketOrderMenu = useMarketOrdersMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
  });

  const columnsWithMenu = useMemo(() => [...columns, ...marketOrderMenu.columns], [columns, marketOrderMenu.columns]);
  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause: filterBuilderAccordion.openClause,
      mixpanel,
    });
  });

  const { user } = useUserContext();

  const request = useMemo(
    () => ({
      name: MARKET_ORDER,
      tag: blotterID,
      ...(parentOrderID != null && { ParentOrderID: parentOrderID }),
      ...(parentRFQID != null && { ParentRFQID: parentRFQID }),
      ...removeEmptyFilters(onlyServerFilterKeys(marketOrderFilter.filter)),
    }),
    [blotterID, parentOrderID, parentRFQID, marketOrderFilter.filter]
  );

  // This call owns the true filter state
  const blotterTable = useWsBlotterTable({
    request,
    persistence: persistedBlotterTable,
    rowID: MarketOrder.rowID,
    clientLocalFilter,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    columns: columnsWithMenu,
    getExtraMainMenuItems,
    pauseParams: {
      showPauseButton: isTalosUser(user),
    },
    contextMenuOptions: {
      getCustomContextMenuItems: marketOrderMenu.getCustomContextMenuItems,
    },
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
    exportOptions: {
      name: 'MarketOrders',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <Flex h="100%" flexDirection="column" overflow="hidden" data-testid="market-orders-blotter">
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        canSubmitClearedDateRange={source === 'trading' ? true : false}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.SubmitTime} />
      <BlotterTable {...blotterTable} />
      {marketOrderMenu.dialogs}
    </Flex>
  );
}
